import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Legal.css';

const CommunityGuidelines: React.FC = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="legal-container">
      <div className="legal-card">
        <div className="legal-header">
          {!localStorage.getItem('token') ? <h1>MINDCAST</h1> : null}
          {!location.pathname.startsWith('/legal') && (
            <div className="legal-nav-dropdown" ref={dropdownRef}>
              <button className="legal-nav-button" onClick={() => setShowDropdown(!showDropdown)}>
                <span className="legal-nav-text">Community Guidelines</span>
                <div className={`legal-nav-icon ${showDropdown ? 'open' : ''}`}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </div>
              </button>

              {showDropdown && (
                <div className="legal-nav-menu">
                  <button className="legal-nav-item" onClick={() => window.location.href = '/terms'}>
                    <span>Terms of Use</span>
                  </button>
                  <button className="legal-nav-item" onClick={() => window.location.href = '/privacy'}>
                    <span>Privacy Policy</span>
                  </button>
                  <button className="legal-nav-item active" onClick={() => window.location.href = '/community-guidelines'}>
                    <span>Community Guidelines</span>
                    <svg className="legal-nav-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {!localStorage.getItem('token') ? (
            <Link to="/register" className="back-link">←</Link>
          ) : null}
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <p>
              These Community Guidelines apply to all users worldwide and to all content, including AI-generated content. They are designed to keep Mindcast safe, respectful, and engaging. Users agree that all content reflects the user's opinion, not Mindcast's, and users are solely responsible for their posts. Mindcast does not endorse, verify, or guarantee the accuracy of user content and is not liable for any user content or interactions.
            </p>
          </section>

          <section className="legal-section">
            <h2>1. Safety & Harm</h2>
            <ul>
              <li>Users may not post content that promotes illegal activity, coordinated harm, or unsafe practices. This includes fraud, scams, deceptive solicitations, or instructions for committing crimes.</li>
              <li>Threats, harassment, bullying, stalking, or intimidation toward other users are prohibited. Content or behavior that could harm or intimidate individuals or groups may result in removal or account suspension.</li>
              <li>Graphic violence, gore, or harmful content is prohibited, except in educational or news contexts with appropriate warnings. Content promoting suicide, self-injury, or eating disorders is also prohibited, though educational or supportive content may be allowed with warnings.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. Respect & Inclusion</h2>
            <ul>
              <li>Hate speech, discrimination, or dehumanizing language targeting users based on race, ethnicity, nationality, religion, gender, sexual orientation, disability, or other protected characteristics is prohibited.</li>
              <li>Users must respect the privacy of others. Sharing personal or sensitive information without consent, impersonating others, or doxxing is prohibited.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Adult Content & Sexual Conduct</h2>
            <ul>
              <li>Sexual exploitation, adult sexual solicitation, or sharing sexual content of non-consenting individuals is prohibited.</li>
              <li>Content involving adult sexual activity may only be shared according to age restrictions and warnings. Content involving minors or sexual exploitation of children is strictly forbidden.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Intellectual Property & Content Responsibility</h2>
            <ul>
              <li>Users may only post content they own or have permission to share. Posting third-party copyrighted content without authorization is prohibited.</li>
              <li>Users are solely responsible for all content they post, including AI-generated content, and must comply with applicable laws. Mindcast is not liable for any claims, damages, or consequences arising from user content.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Account Integrity & Platform Safety</h2>
            <ul>
              <li>Users must maintain authentic accounts. Fake accounts, impersonation, or misrepresentation are prohibited. Parody or fan accounts must clearly indicate their nature.</li>
              <li>Users may not use bots, scripts, or other methods to manipulate metrics, hack the platform, or compromise cybersecurity.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Content Relevance & Spam</h2>
            <ul>
              <li>Content should be relevant to books, podcasts, and ideas. Spam, repetitive posts, or unrelated content that disrupts the community experience is prohibited.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Enforcement, Reporting & Liability</h2>
            <ul>
              <li>Mindcast may, at its sole discretion and without prior notice, remove content, restrict access, suspend, or terminate accounts that violate these guidelines or pose a risk to the community.</li>
              <li>Mindcast may act on user reports of violations but is under no obligation to do so and may act or not act at its discretion.</li>
              <li>Mindcast is not responsible for any losses, damages, or consequences resulting from enforcement actions, user content, or user interactions. Users acknowledge that enforcement decisions are final and may occur without prior warning.</li>
              <li>Mindcast reserves the right to update, modify, or add rules at any time to protect the safety, integrity, and quality of the platform. Users are responsible for reviewing and complying with updated guidelines.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Global Applicability</h2>
            <ul>
              <li>Users accessing Mindcast from jurisdictions outside Canada are responsible for complying with local laws. Mindcast makes no representation regarding the enforceability of these guidelines outside Canada.</li>
            </ul>
          </section>
        </div>

        <div className="legal-footer">
          <p>By using Mindcast, you acknowledge that you have read and understood these community guidelines.</p>
          <p className="legal-subtitle">Last Updated: September 27th, 2025</p>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelines;
